import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import Chart from 'react-apexcharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageLoading from '../../../common/componentsV2/elements/PageLoading';
import {
  RcQuotesStyles,
} from './BackOfficeVehicleQuotes.styles';
import {
  addBackOfficeQuoteToCart,
  loadBackOfficeQuotes,
  removeBackOfficeQuoteFromCart,
  resetBackOfficeQuotes,
} from '../../../features/backofficeResults/backofficeResultsActions';
import { Layout } from '../components/Layout/Layout';
import {
  AvvisoAlert,
  ConsulenzaReportButton,
} from '../../RcQuotes.styles';
import {
  commercialExtraQuoteTypes,
  commercialChartKeys,
  safeCapitalizeWords,
} from '../../../common/constants';
import { confirmAlert } from '../../../common/utils/alerts';
import {
  RiskCard,
  RiskContainer,
} from '../../../common/components/redesignComponents/ResultPageComponents/v2/ResultSlotCard.styles';
import { EstimateContainer } from '../../../common/components/redesignComponents/common/Redesign.styles';
import SelectedQuotesCardsBackoffice from '../../../common/components/redesignComponents/ResultPageComponents/SelectedQuotesCardsBackoffice';
import ButtonComponentLogicBackoffice from '../../../common/components/redesignComponents/ResultPageComponents/ButtonComponentLogicBackoffice';
import { riskCardStyles } from '../../../common/components/redesignComponents/ResultPageComponents/RcQuotesModal.style';
import ModalQuotesBackoffice from '../../../common/components/redesignComponents/ResultPageComponents/ModalQuotesBackoffice';
import { apiDownloadCommercialReportDocument } from '../../../services/axios-client/axeCommons';
import { getQuoteTypes } from '../../../features/utilities/utilitiesActions';
import { V2FontImport } from '../../../common/formInputsV2/V2Fonts';
import ResultOverviewRow from '../../../common/components/redesignComponents/ResultPageComponents/v2/ResultOverviewRow';
import CommercialSummaryCard from '../../../common/components/redesignComponents/ResultPageComponents/v2/CommercialSummaryCard';
import CommercialQuoteCostSummaryCard from '../../../common/components/redesignComponents/ResultPageComponents/v2/CommercialQuoteCostSummaryCard';
import FailedQuotationsComponent from '../../../common/components/redesignComponents/ResultPageComponents/v2/FailedQuotationsComponent';

function BackOfficeCommercialQuotes() {
  const location = useLocation();
  const [currentQuotes] = useState('diretta');
  const [counter, setCounter] = useState(180);

  const { quoteTypes } = useSelector((store) => store.utilities.quoteTypes);

  const { requestToken } = useParams();
  const state = useSelector(
    (store) => store.backofficeResults.back_office_quotes,
  );
  const {
    loading,
    requestInfo,
    quotes,
    failedQuotes,
    processingQuote,
    addingQuote,
    risk_quotations,
    commercial_company_report,
    companyData,
    sortBy,
  } = state;

  const [chartData, setChartData] = useState([]);
  const [chartCategories, setChartCategories] = useState([]);

  const [showConsulenzaReport, setShowConsulenzaReport] = useState(false);
  const [showUbicazioneQuotes, setShowUbicazioneQuotes] = useState(false);

  const optionsChart = {
    chart: {
      type: 'bar',
    },
    plotOptions: {
      bar: {
        distributed: true,
        dataLabels: {
          position: 'top',
        },
      },
    },
    colors: chartData.map((value) => {
      if (value >= 66) return '#FF0000'; // Red
      if (value <= 25) return '#00A651'; // Green
      return '#FFE81A'; // Yellow
    }),
    dataLabels: {
      enabled: true,
      formatter(val) {
        return `${val}%`;
      },
      offsetY: -20,
      style: {
        fontSize: '14px',
        colors: ['#282828'],
      },
    },
    xaxis: {
      categories: chartCategories,
    },
  };

  const series = [
    {
      name: 'Valori',
      data: chartData,
    },
  ];

  useEffect(() => {
    if (commercial_company_report !== null) {
      const newArrData = [];
      const newArrCats = [];

      const keys_to_remove = [
        'id',
        'commercial_company_id',
        'insurance_request_id',
        'broker_agent_id',
        'created_at',
        'updated_at',
        'deleted_at',
      ];

      // object after removing columns
      const newObject = Object.fromEntries(
        Object.entries(commercial_company_report).filter(
          ([key]) => !keys_to_remove.includes(key),
        ),
      );

      Object.entries(newObject).forEach(([key, value]) => {
        // Push category names (keys) to newArrCats

        newArrCats.push(commercialChartKeys.find((k) => k.id === key).name);

        // Push positive values (you can also include negative if you want)
        let val = value.positive - value.negative;

        if (val > 100) {
          val = 100;
        }

        if (val < 0) {
          val = 0;
        }

        newArrData.push(val);
      });

      setChartData(newArrData);
      setChartCategories(newArrCats);
    }
  }, [commercial_company_report]);

  const toggleAutoReportChart = () => {
    setShowConsulenzaReport(!showConsulenzaReport);
  };

  useEffect(() => {
    loadBackOfficeQuotes(requestToken);
    getQuoteTypes();

    return () => {
      resetBackOfficeQuotes();
    };
  }, []);

  const refresh = () => loadBackOfficeQuotes(requestToken);

  const fetchQuotes = () => {
    loadBackOfficeQuotes(requestToken);
  };

  useEffect(() => {
    const timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  const selectQuotation = (quoteId, closeButton) => {
    confirmAlert('Sei Sicuro di aggiungere questa quotazione?', () => addBackOfficeQuoteToCart(quoteId, fetchQuotes, closeButton));
  };

  const removeItemFromCart = (quoteId) => {
    confirmAlert('Sei Sicuro di rimuovere questa quotazione?', () => removeBackOfficeQuoteFromCart(quoteId, fetchQuotes));
  };

  const exportChartToPDF = async () => {
    const response = await apiDownloadCommercialReportDocument(
      requestInfo.id,
    );
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  const getTotal = () => {
    let quoteInProccessAmount = 0;
    let risksTotalAmount = 0;

    if (processingQuote !== null && processingQuote.amount !== null) {
      quoteInProccessAmount = parseFloat(processingQuote.amount);
    }

    const riskTypes = quoteTypes.filter((t) => t.id !== 'res_civile');

    for (let x = 0; x < riskTypes.length; x++) {
      const type = riskTypes[x];
      if (risk_quotations && risk_quotations[type.id] !== undefined) {
        for (let i = 0; i < risk_quotations[type.id].length; i++) {
          const item = risk_quotations[type.id][i];

          risksTotalAmount += parseFloat(item.amount);
        }
      }
    }

    return (quoteInProccessAmount + risksTotalAmount).toFixed(2);
  };

  const hasExtraQuotes = quotes && commercialExtraQuoteTypes.some((type) => quotes[type.id]);
  const hasExtraRiskQuotes = risk_quotations
    && commercialExtraQuoteTypes.some((type) => risk_quotations[type.id]);

  const currTypes = processingQuote !== null
    ? requestInfo.quotations?.filter((q) => q.company_id === processingQuote.company_id)?.map((el) => el.quote_type)
    : [];
  const showExtraQuotes = processingQuote === null || processingQuote.company_id !== 1001;

  if (loading) {
    return <PageLoading />;
  }

  return (
    <Layout>
      <V2FontImport />
      <RcQuotesStyles>
        <div className="resultsPage">
          <ResultOverviewRow>
            <CommercialSummaryCard
              companyData={companyData}
              policyEffectiveDate={requestInfo.policy_effective_date}
            />
            <CommercialQuoteCostSummaryCard
              processingQuote={processingQuote}
              quoteTypes={quoteTypes}
              riskQuotations={risk_quotations}
              commercialExtraQuoteTypes={commercialExtraQuoteTypes}
              hasExtraRiskQuotes={hasExtraRiskQuotes}
              getTotal={getTotal}
            />
          </ResultOverviewRow>

          {/* //todo  check again with data  */}
          {commercial_company_report !== null && (
            <div className="row">
              <div className="col-12">
                <EstimateContainer>
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div>
                      <div className="d-flex align-items-center justify-content-start flex-column flex-xxl-row mt-2">
                        <div className="selling-distance mt-2 mt-xxl-0 purchase-quote-third-step">
                          {requestInfo.product_id === '10' && (
                            <>
                              <ConsulenzaReportButton
                                className="btn btn-success m-2"
                                onClick={() => toggleAutoReportChart()}
                              >
                                Consulenza Report
                              </ConsulenzaReportButton>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {showConsulenzaReport ? (
                    <div className="mt-2">
                      <h4 className="text-center fw-bold mb-0">
                        Analisi Bisogni Cliente
                      </h4>
                      <button
                        className="btn btn-success m-2"
                        onClick={() => exportChartToPDF()}
                      >
                        Scarica PDF
                      </button>
                      <div id="chart-container" style={{ minHeight: '365px' }}>
                        <Chart
                          options={optionsChart}
                          series={series}
                          height="350px"
                          type="bar"
                        />
                      </div>
                    </div>
                  ) : (
                    ''
                  )}
                </EstimateContainer>
              </div>
            </div>
          )}

          <div>
            {location.state != null && location.state.errorMessage != null && (
              <div className="alert alert-danger mt-3 rounded-pill">
                {location.state.errorMessage}
              </div>
            )}
          </div>

          {(quotes === null || !quotes.res_civile)
            && (
              <AvvisoAlert role="alert">
                <h4>Avviso!</h4>
                <p>Il preventivo non è riuscito per problemi relativi ai dati assicurativi. Si prega di contattare il nostro backoffice per ottenere il preventivo o ulteriori informazioni.</p>
              </AvvisoAlert>
            )}

          <RiskContainer className="mb-4 ">
            {quoteTypes.filter((type) => processingQuote === null || type.id === 'res_civile' || currTypes?.includes(type.id)).map((type, index) => (
              <React.Fragment key={index}>
                {quotes && quotes[type.id] && (
                  <RiskCard
                    className={riskCardStyles(
                      requestInfo,
                      type,
                      risk_quotations,
                    )}
                  >
                    <div className="me-auto">
                      <h5>{safeCapitalizeWords(type.name)}</h5>
                    </div>
                    <SelectedQuotesCardsBackoffice
                      processingQuote={processingQuote}
                      type={type}
                      refresh={refresh}
                      riskQuotations={risk_quotations}
                      requestInfo={requestInfo}
                    />

                    <ButtonComponentLogicBackoffice
                      type={type}
                      processingQuote={processingQuote}
                      removeItemFromCart={removeItemFromCart}
                      requestInfo={requestInfo}
                      riskQuotations={risk_quotations}
                    />
                  </RiskCard>
                )}
              </React.Fragment>
            ))}
          </RiskContainer>

          {hasExtraQuotes && showExtraQuotes && (
            <>
              <div className="pt-2">
                <hr className="my-3" />
              </div>
              <div className="d-flex align-items-center justify-content-start gap-5">
                <h4 className="text-start fw-bold m-0">
                  Garanzie operanti a livello di ubicazione
                </h4>
                <button
                  className="btn btn-success px-4"
                  onClick={() => setShowUbicazioneQuotes(!showUbicazioneQuotes)}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <p className="m-0">Apri</p>
                    <FontAwesomeIcon
                      icon={showUbicazioneQuotes ? faChevronUp : faChevronDown}
                    />
                  </div>
                </button>
              </div>
            </>
          )}
          {showUbicazioneQuotes && showExtraQuotes && (
            <RiskContainer className="mb-4 ">
              {commercialExtraQuoteTypes.map((type, index) => (
                <React.Fragment key={index}>
                  {quotes && quotes[type.id] && (
                    <RiskCard
                      className={riskCardStyles(
                        requestInfo,
                        type,
                        risk_quotations,
                      )}
                    >
                      <div className="me-auto">
                        <h5>{type.name}</h5>
                      </div>
                      <SelectedQuotesCardsBackoffice
                        processingQuote={processingQuote}
                        type={type}
                        refresh={refresh}
                        riskQuotations={risk_quotations}
                        requestInfo={requestInfo}
                      />

                      <ButtonComponentLogicBackoffice
                        type={type}
                        processingQuote={processingQuote}
                        removeItemFromCart={removeItemFromCart}
                        requestInfo={requestInfo}
                        riskQuotations={risk_quotations}
                      />
                    </RiskCard>
                  )}
                </React.Fragment>
              ))}
            </RiskContainer>
          )}
          <div className="my-4">
            {quoteTypes.map((type, index) => (
              <ModalQuotesBackoffice
                processingQuote={processingQuote}
                key={index}
                type={type}
                addingQuote={addingQuote}
                sortBy={sortBy}
                sortQuotes={() => { }}
                handleCurrentQuotesDisplay={() => { }}
                currentQuotes={currentQuotes}
                requestInfo={requestInfo}
                counter={counter}
                quotations={quotes}
                requestToken={requestToken}
                refresh={refresh}
                selectQuotation={selectQuotation}
              />
            ))}
            {showExtraQuotes && commercialExtraQuoteTypes.map((type, index) => (
              <ModalQuotesBackoffice
                processingQuote={processingQuote}
                key={index}
                type={type}
                addingQuote={addingQuote}
                sortBy={sortBy}
                sortQuotes={() => { }}
                handleCurrentQuotesDisplay={() => { }}
                currentQuotes={currentQuotes}
                requestInfo={requestInfo}
                counter={counter}
                quotations={quotes}
                requestToken={requestToken}
                refresh={refresh}
                selectQuotation={selectQuotation}
              />
            ))}
          </div>

          {failedQuotes !== null && failedQuotes?.length > 0 && (
            <FailedQuotationsComponent failedQuotes={failedQuotes} />
          )}
        </div>
      </RcQuotesStyles>
    </Layout>
  );
}

export default BackOfficeCommercialQuotes;
